/**
 * sidebar-manager.js
 * - Storefront (live): MOVE sidebar content into slot (fast, single DOM)
 * - Theme Editor (designMode): CLONE sidebar content into slot (stable on re-render)
 *
 * Markup requirements:
 * 1) Blog layout owner (blog section / template):
 *    <aside id="sidebar-section" data-content="sidebar"></aside>
 *
 * 2) Sidebar section (content provider):
 *    <div class="sidebar-template" data-content="sidebar">...</div>
 *
 * Canvas (optional):
 *  - Open button: .btn-sidebar
 *  - Sidebar panel: .canvas-sidebar
 *  - Overlay: .overlay-canvas
 *  - Close button: .icon-close-sb
 */

class SidebarManager extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    // Editor events that may happen without full page reload
    this._editorEvents = [
      'shopify:section:load',
      'shopify:section:reorder',
      'shopify:section:select',
      'shopify:section:deselect',
      'shopify:block:select',
      'shopify:block:deselect',
    ];
    this._editorHandler = (e) => this.init(e?.target || document);
  }

  connectedCallback() {
    // Prevent double-binding if element is re-connected
    if (this._bound) return;
    this._bound = true;

    // Init once on connect
    this.init(document);

    // Global delegation (Dawn-like)
    document.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);

    // Theme editor: re-init when sections/blocks are changed
    this._editorEvents.forEach((evt) => {
      document.addEventListener(evt, this._editorHandler);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);

    this._editorEvents.forEach((evt) => {
      document.removeEventListener(evt, this._editorHandler);
    });
  }

  isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  init(root = document) {
    this.injectSidebars(root);
  }

  /**
   * Finds each .sidebar-template[data-content]
   * and injects into matching #sidebar-section[data-content="..."] slot.
   */
  injectSidebars(root = document) {
    const sources = root.querySelectorAll('.sidebar-template[data-content]');
    if (!sources.length) return;

    sources.forEach((src) => {
      const key = src.getAttribute('data-content');
      if (!key) return;

      const target = document.querySelector(
        `#sidebar-section[data-content="${CSS.escape(key)}"]`
      );
      if (!target) return;

      if (this.isDesignMode()) {
        // Theme Editor: CLONE (stable when layout re-renders)
        // overwrite content each time so it never goes stale
        target.innerHTML = src.innerHTML;

        // hide source so UI doesn't duplicate
        src.hidden = true;
        src.setAttribute('aria-hidden', 'true');

        // (Optional) mark injected to help debug
        src.dataset.sidebarInjected = 'true';
        return;
      }

      // Storefront: MOVE (fast, only one DOM)
      target.textContent = '';
      while (src.firstChild) target.appendChild(src.firstChild);
      src.remove();
    });
  }

  // ---------- Canvas sidebar controls ----------
  openCanvas() {
    const sidebar = document.querySelector('.canvas-sidebar');
    const overlay = document.querySelector('.overlay-canvas');
    if (sidebar) sidebar.classList.add('show');
    if (overlay) overlay.classList.add('show');
    document.documentElement.classList.add('sidebar-canvas-open');
  }

  closeCanvas() {
    const sidebar = document.querySelector('.canvas-sidebar');
    const overlay = document.querySelector('.overlay-canvas');
    if (sidebar) sidebar.classList.remove('show');
    if (overlay) overlay.classList.remove('show');
    document.documentElement.classList.remove('sidebar-canvas-open');
  }

  onClick(e) {
    // Open canvas
    const openBtn = e.target.closest('.btn-sidebar');
    if (openBtn) {
      e.preventDefault();
      this.openCanvas();
      return;
    }

    // Close canvas
    const closeBtn = e.target.closest('.icon-close-sb');
    const overlay = e.target.closest('.overlay-canvas');
    if (closeBtn || overlay) {
      e.preventDefault();
      this.closeCanvas();
    }
  }

  onKeydown(e) {
    if (e.key !== 'Escape') return;
    const sidebar = document.querySelector('.canvas-sidebar');
    if (sidebar && sidebar.classList.contains('show')) this.closeCanvas();
  }
}

if (!customElements.get('sidebar-manager')) {
  customElements.define('sidebar-manager', SidebarManager);
}
